import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { AUTO_FOCUS_DELAY_MS } from '../constants.js';

/**
 * Programmatically focus an element after a short delay. The native
 * `<dialog>.showModal()` steals focus on mount, so `autoFocus` alone
 * doesn't work inside the funnel shell. This hook waits for the
 * stagger animation to settle before transferring focus.
 *
 * Returns a ref the caller attaches to the target element.
 *
 * Per ai.knowledge rules/global/stay-in-scope.md -- deduplicates the
 * 500ms setTimeout+focus pattern previously inlined in four screens.
 */
export const useAutoFocus = <T extends HTMLElement>(
  delayMs: number = AUTO_FOCUS_DELAY_MS
): RefObject<T> => {
  // useRef<T>(null) returns RefObject<T | null> in React 19+ types.
  // The null initial is required but the ref is always assigned before
  // the consumer reads it, so the cast is safe.
  const ref = useRef<T>(null) as RefObject<T>;

  useEffect(() => {
    const timer = setTimeout(() => ref.current?.focus(), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs]);

  return ref;
};
